import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { bareChatId, conversationQueryKey, useConversation } from '../../hooks/useConversation';
import { agentClient } from '../../lib/connect/clients';
// How long the "Saved" indicator stays mounted after a successful save.
// 2s is long enough to register as feedback without lingering past the
// next interaction.
const SAVED_INDICATOR_MS = 2000;
export function ChatSettings({ chatId, isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('general');
    const queryClient = useQueryClient();
    const bareId = bareChatId(chatId);
    // useConversation is shared with the chat header so a Save here lights
    // up the header title instantly via setQueryData below.
    const conversationQuery = useConversation(chatId);
    const fetched = {
        title: conversationQuery.data?.conversation?.title ?? '',
        description: conversationQuery.data?.conversation?.description ?? ''
    };
    // Form holds the working copy; `baseline` is what we last loaded or
    // last successfully saved. Dirty = form !== baseline (field-wise).
    const [form, setForm] = useState(fetched);
    const [baseline, setBaseline] = useState(fetched);
    const [showSavedIndicator, setShowSavedIndicator] = useState(false);
    // Tracks the last attempted-but-failed refetch after a successful save.
    // Distinct from updateMutation.error (which fires when Save itself fails)
    // — this one fires when Save succeeded but the post-save read couldn't
    // confirm what's on the server. Surfaces as a "saved but display may be
    // stale" notice so the user knows to reload if it matters.
    const [refetchFailed, setRefetchFailed] = useState(false);
    // Sync form + baseline when the query resolves (or re-resolves after
    // an invalidation). Only rehydrate from the fetched value — never
    // clobber a dirty in-progress edit with whatever the network returned.
    // The check compares baseline (last-saved) to fetched (server) so an
    // external rename does propagate when the user hasn't touched the form.
    //
    // Dependency contract: this effect MUST depend on the primitive
    // fetched.title / fetched.description values, NOT on the `fetched`
    // object ref. `fetched` is recomputed on every render so depending on
    // the ref would cause an infinite re-run. A future refactor that
    // wraps `fetched` in useMemo could re-introduce ref-dependency
    // accidentally — keep the primitive deps.
    // biome-ignore lint/correctness/useExhaustiveDependencies: rehydrate only on fetched value change
    useEffect(()=>{
        if (!conversationQuery.isSuccess) return;
        const dirty = form.title !== baseline.title || form.description !== baseline.description;
        if (dirty) return;
        setForm(fetched);
        setBaseline(fetched);
    }, [
        conversationQuery.isSuccess,
        fetched.title,
        fetched.description
    ]);
    const updateMutation = useMutation({
        mutationFn: async (next)=>{
            return agentClient.updateConversation({
                id: bareId,
                title: next.title,
                description: next.description
            });
        },
        onSuccess: async (resp, next)=>{
            // Reset the working copy to what was actually persisted (next.title
            // is the trimmed value; the user may have typed "Foo " and the
            // baseline-set-only path left form.title = "Foo " while baseline
            // became "Foo", which kept dirty=true and Save enabled forever).
            // Setting both form and baseline to next collapses both back to
            // the canonical post-save state.
            setForm(next);
            setBaseline(next);
            setShowSavedIndicator(true);
            setRefetchFailed(false);
            // Optimistic cache writes: the header (useConversation) and the
            // sidebar (['chats']) both render off React Query data, so writing
            // the new title into the caches makes the rename visible across
            // the whole UI before the refetch round-trip lands.
            const respConv = resp.conversation;
            if (respConv) {
                queryClient.setQueryData(conversationQueryKey(chatId), {
                    ...resp,
                    conversation: {
                        ...respConv,
                        title: next.title,
                        description: next.description
                    }
                });
            }
            queryClient.setQueryData([
                'chats'
            ], (prev)=>{
                if (!prev) return prev;
                const wireId = `shanks-${bareId}`;
                const touched = prev.chats.map((c)=>c.chatId === wireId || c.chatId === bareId ? {
                        ...c,
                        title: next.title
                    } : c);
                return {
                    ...prev,
                    chats: touched
                };
            });
            // Confirm the optimistic write against the server. A failure here
            // doesn't mean Save failed — Save already succeeded — but it means
            // the caches we just wrote are unverified, so we warn the user
            // their displayed state may not match the server.
            //
            // throwOnError is required: react-query 5's refetchQueries default
            // resolves the promise with a result array even when individual
            // queries error. Without throwOnError our try/catch would never
            // fire and the user would never see the stale-data warning.
            try {
                await Promise.all([
                    queryClient.refetchQueries({
                        queryKey: [
                            'chats'
                        ]
                    }, {
                        throwOnError: true
                    }),
                    queryClient.refetchQueries({
                        queryKey: conversationQueryKey(chatId)
                    }, {
                        throwOnError: true
                    })
                ]);
            } catch  {
                setRefetchFailed(true);
            }
        }
    });
    useEffect(()=>{
        if (!showSavedIndicator) return;
        const t = window.setTimeout(()=>setShowSavedIndicator(false), SAVED_INDICATOR_MS);
        return ()=>window.clearTimeout(t);
    }, [
        showSavedIndicator
    ]);
    // ChatSettings stays mounted across modal close/open (gated by isOpen
    // returning null, not by unmount), so transient per-open state has to
    // be reset explicitly when isOpen flips to true. Without this, a save
    // whose post-save refetch errored leaves the amber "couldn't confirm"
    // banner visible the next time the user opens the modal, even after
    // they navigated away and back. The "Saved" indicator gets the same
    // treatment for the same reason — its 2s timer may have fired during
    // a previous open, but a fresh open shouldn't replay it.
    useEffect(()=>{
        if (!isOpen) return;
        setRefetchFailed(false);
        setShowSavedIndicator(false);
    }, [
        isOpen
    ]);
    const dirty = form.title !== baseline.title || form.description !== baseline.description;
    // A title that's empty or whitespace-only cannot be saved — the handler
    // would no-op silently otherwise, leaving the user wondering. Compute
    // it here so the input, the save button, and the inline-validation
    // message agree on the same predicate.
    const titleEmpty = form.title.trim() === '';
    const handleClose = useCallback(()=>{
        if (dirty) {
            const ok = window.confirm('You have unsaved changes. Discard them?');
            if (!ok) return;
            // Revert the working copy so reopening doesn't show stale edits.
            setForm(baseline);
        }
        onClose();
    }, [
        dirty,
        baseline,
        onClose
    ]);
    const handleSave = useCallback(()=>{
        // The Save button is disabled when titleEmpty is true, so this is a
        // belt-and-braces guard — keyboard-driven submits (Enter in a future
        // wired-up form) shouldn't be able to bypass it.
        const trimmedTitle = form.title.trim();
        if (trimmedTitle === '') return;
        updateMutation.mutate({
            title: trimmedTitle,
            description: form.description
        });
    }, [
        form.title,
        form.description,
        updateMutation
    ]);
    if (!isOpen) return null;
    const saving = updateMutation.isPending;
    const saveError = updateMutation.error;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-black/50 z-50 flex items-center justify-center",
        "data-testid": Testids.chatSettings.dialog,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 min-h-[720px] max-h-[85vh] flex flex-col",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Settings, {
                                    className: "w-5 h-5 text-gray-700 dark:text-gray-300"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-xl font-semibold text-gray-900 dark:text-white",
                                    children: "Chat Settings"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: handleClose,
                            className: "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors",
                            "aria-label": "Close settings",
                            children: /*#__PURE__*/ _jsx(X, {
                                className: "w-6 h-6"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "border-b border-gray-200 dark:border-gray-700",
                    children: /*#__PURE__*/ _jsxs("nav", {
                        className: "flex px-6 -mb-px",
                        "aria-label": "Settings tabs",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setActiveTab('general'),
                                className: `py-4 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'general' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`,
                                children: "General"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>setActiveTab('permissions'),
                                className: `py-4 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'permissions' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`,
                                children: "Permissions"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 overflow-y-auto p-6",
                    children: [
                        activeTab === 'general' && /*#__PURE__*/ _jsx(GeneralTab, {
                            form: form,
                            onChange: setForm,
                            loading: conversationQuery.isLoading,
                            // Only treat a query error as a full-replacement load failure
                            // when we have NEVER successfully loaded. A failure on the
                            // post-save refetch surfaces as the stale-warning banner
                            // (below) instead — we don't want to wipe the form the user
                            // just saved into because a confirm-read tripped a network.
                            //
                            // `isSuccess` flips back to false when a refetch errors, so
                            // we gate on `data !== undefined` instead — data persists
                            // through the error transition in react-query 5.
                            //
                            // TODO(audit#3): this also hides genuine 404/auth errors
                            // that happen *after* the initial successful load (e.g. the
                            // chat was deleted in another tab while this modal was
                            // open). The user sees stale form data with no signal.
                            // Fix is to track the cause of the error separately
                            // (CodeNotFound / CodePermissionDenied → full-replacement
                            // load-error; everything else → stale-data warning).
                            loadError: conversationQuery.data !== undefined ? null : conversationQuery.error,
                            saveError: saveError,
                            titleEmpty: titleEmpty && dirty,
                            refetchFailed: refetchFailed
                        }),
                        activeTab === 'permissions' && /*#__PURE__*/ _jsx("div", {
                            className: "text-sm text-gray-600 dark:text-gray-400",
                            children: /*#__PURE__*/ _jsx("p", {
                                children: "Permission settings coming soon..."
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700",
                    children: [
                        showSavedIndicator && /*#__PURE__*/ _jsx("span", {
                            className: "text-sm text-green-600 dark:text-green-400",
                            "data-testid": Testids.chatSettings.savedIndicator,
                            "aria-live": "polite",
                            children: "Saved"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: handleClose,
                            "data-testid": Testids.chatSettings.closeButton,
                            className: "px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors",
                            children: dirty ? 'Cancel' : 'Close'
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: handleSave,
                            disabled: !dirty || titleEmpty || saving || conversationQuery.isLoading || bareId === '',
                            "data-testid": Testids.chatSettings.saveButton,
                            className: "px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors",
                            children: saving ? 'Saving…' : 'Save'
                        })
                    ]
                })
            ]
        })
    });
}
function GeneralTab({ form, onChange, loading, loadError, saveError, titleEmpty, refetchFailed }) {
    if (loading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center py-12 text-gray-500 dark:text-gray-400",
            children: "Loading…"
        });
    }
    if (loadError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-red-600 dark:text-red-400 p-4 border border-red-200 dark:border-red-900/30 rounded-lg bg-red-50 dark:bg-red-900/10",
            children: "Failed to load chat settings. Close and reopen to try again."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            refetchFailed && /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-amber-700 dark:text-amber-300 p-3 border border-amber-200 dark:border-amber-900/30 rounded-lg bg-amber-50 dark:bg-amber-900/10",
                "data-testid": Testids.chatSettings.staleWarning,
                "aria-live": "polite",
                children: "Your changes were saved, but we couldn't confirm them with the server. The values shown here may be out of date — reload the page to refresh."
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        htmlFor: "chat-name",
                        className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
                        children: "Chat Name"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        id: "chat-name",
                        type: "text",
                        value: form.title,
                        onChange: (e)=>onChange({
                                ...form,
                                title: e.target.value
                            }),
                        "data-testid": Testids.chatSettings.nameInput,
                        "aria-invalid": titleEmpty,
                        "aria-describedby": titleEmpty ? 'chat-name-empty' : undefined,
                        placeholder: "Enter chat name...",
                        className: `w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent ${titleEmpty ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`
                    }),
                    titleEmpty && /*#__PURE__*/ _jsx("p", {
                        id: "chat-name-empty",
                        className: "mt-1 text-sm text-red-600 dark:text-red-400",
                        "data-testid": Testids.chatSettings.titleEmptyMessage,
                        children: "Chat name cannot be empty."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        htmlFor: "chat-description",
                        className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
                        children: "Description"
                    }),
                    /*#__PURE__*/ _jsx("textarea", {
                        id: "chat-description",
                        rows: 3,
                        value: form.description,
                        onChange: (e)=>onChange({
                                ...form,
                                description: e.target.value
                            }),
                        "data-testid": Testids.chatSettings.descriptionInput,
                        placeholder: "Enter description...",
                        className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    })
                ]
            }),
            saveError != null && /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-red-600 dark:text-red-400 p-3 border border-red-200 dark:border-red-900/30 rounded-lg bg-red-50 dark:bg-red-900/10",
                "data-testid": Testids.chatSettings.errorMessage,
                children: saveError instanceof Error ? saveError.message : 'Failed to save chat settings.'
            })
        ]
    });
}
